/**
 * FollowupEmailDrafts — on-demand, per-recipient follow-up email drafts.
 *
 * Draft-only by design: Rithmo never sends. The user picks a recipient, picks a
 * tone, and generates a personalized draft grounded in the call transcript plus the
 * durable context Rithmo holds on that person (commitments both ways, personal
 * facts, prior meetings). The draft is editable; Copy and "Open in mail client"
 * (mailto) hand it off. Personal facts the model used are shown as provenance.
 *
 * Self-contained: it fetches its own recipient list (which inlines any existing
 * draft) from /meetings/:id/followup-drafts, so it needs only the meetingId.
 *
 * Once a draft exists, a chat panel lets the sender refine it in place ("make it
 * shorter", "lead with the intro to Dana") without starting over: a mini-GPT scoped
 * to this one email, with persisted conversation memory.
 */

import { useEffect, useMemo, useState } from 'react';
import { meetingsAPI } from '../../services/api';
import { decodeHtmlEntities } from '../../utils/displayText';
import {
  Mail,
  Sparkles,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  Pencil,
  RotateCcw,
  Send,
  MessageSquare,
} from 'lucide-react';

const TONES = [
  { id: 'warm', label: 'Warm' },
  { id: 'concise', label: 'Concise' },
  { id: 'formal', label: 'Formal' },
];

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function firstNameOf(name) {
  const first = String(name || '').trim().split(/\s+/)[0];
  return first || 'them';
}

export default function FollowupEmailDrafts({ meetingId }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Per-recipient working state, keyed by participantId.
  const [instructions, setInstructions] = useState({});
  const [tones, setTones] = useState({});
  const [drafts, setDrafts] = useState({});
  const [generating, setGenerating] = useState({});
  const [genError, setGenError] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [copied, setCopied] = useState(false);

  // Refinement chat state, keyed by participantId.
  const [chatTurns, setChatTurns] = useState({});
  const [chatLoaded, setChatLoaded] = useState({});
  const [chatInput, setChatInput] = useState({});
  const [chatSending, setChatSending] = useState({});
  const [chatError, setChatError] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    meetingsAPI
      .listFollowupDrafts(meetingId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.recipients ?? [];
        setRecipients(list);
        const seededDrafts = {};
        const seededTones = {};
        const seededInstructions = {};
        for (const r of list) {
          if (r.draft) {
            seededDrafts[r.participantId] = r.draft;
            seededTones[r.participantId] = r.draft.tone;
            if (r.draft.instruction) seededInstructions[r.participantId] = r.draft.instruction;
          }
        }
        setDrafts(seededDrafts);
        setTones(seededTones);
        setInstructions(seededInstructions);
        if (list.length > 0) setSelectedId((prev) => prev ?? list[0].participantId);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load recipients');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  const selected = useMemo(
    () => recipients.find((r) => r.participantId === selectedId) ?? null,
    [recipients, selectedId],
  );
  const selectedDraft = selectedId ? drafts[selectedId] ?? null : null;
  const selectedTone = (selectedId && tones[selectedId]) || 'warm';
  const selectedInstruction = (selectedId && instructions[selectedId]) || '';
  const selectedChat = (selectedId && chatTurns[selectedId]) || [];

  // Load the refinement conversation when a recipient with an existing draft is
  // selected (drafts inline-loaded at mount don't carry their chat). Fetch once.
  useEffect(() => {
    if (!selectedId || !selectedDraft || chatLoaded[selectedId]) return;
    let cancelled = false;
    meetingsAPI
      .getFollowupChat(meetingId, selectedId)
      .then((res) => {
        if (cancelled) return;
        setChatTurns((s) => ({ ...s, [selectedId]: res?.data?.turns ?? [] }));
      })
      .catch(() => {
        if (!cancelled) setChatTurns((s) => ({ ...s, [selectedId]: s[selectedId] ?? [] }));
      })
      .finally(() => {
        if (!cancelled) setChatLoaded((s) => ({ ...s, [selectedId]: true }));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, selectedDraft, chatLoaded, meetingId]);

  async function handleGenerate(participantId) {
    const instruction = (instructions[participantId] || '').trim();
    if (!instruction) {
      setGenError((s) => ({ ...s, [participantId]: 'Tell Rithmo what this email should accomplish first.' }));
      return;
    }
    setGenerating((s) => ({ ...s, [participantId]: true }));
    setGenError((s) => ({ ...s, [participantId]: null }));
    try {
      const res = await meetingsAPI.generateFollowupDraft(meetingId, participantId, instruction, tones[participantId] || 'warm');
      const draft = res?.data?.draft;
      if (!draft) throw new Error('No draft returned');
      setDrafts((s) => ({ ...s, [participantId]: draft }));
      setEditing((s) => ({ ...s, [participantId]: false }));
      // A full regenerate clears the server-side conversation; mirror that locally.
      setChatTurns((s) => ({ ...s, [participantId]: [] }));
      setChatLoaded((s) => ({ ...s, [participantId]: true }));
      setChatError((s) => ({ ...s, [participantId]: null }));
    } catch (err) {
      setGenError((s) => ({ ...s, [participantId]: err?.message || 'Generation failed' }));
    } finally {
      setGenerating((s) => ({ ...s, [participantId]: false }));
    }
  }

  function updateDraftField(participantId, field, value) {
    setDrafts((s) => ({ ...s, [participantId]: { ...s[participantId], [field]: value } }));
  }

  async function handleSaveEdits(participantId) {
    const draft = drafts[participantId];
    if (!draft) return;
    setSaving((s) => ({ ...s, [participantId]: true }));
    try {
      const res = await meetingsAPI.saveFollowupDraft(meetingId, participantId, draft.subject, draft.body);
      if (res?.data?.draft) setDrafts((s) => ({ ...s, [participantId]: res.data.draft }));
      setEditing((s) => ({ ...s, [participantId]: false }));
    } catch (err) {
      setGenError((s) => ({ ...s, [participantId]: err?.message || 'Save failed' }));
    } finally {
      setSaving((s) => ({ ...s, [participantId]: false }));
    }
  }

  async function handleSendChat(participantId) {
    const message = (chatInput[participantId] || '').trim();
    if (!message || chatSending[participantId]) return;
    // Optimistically show the sender's message; reconcile with the server on success.
    const optimistic = { id: `pending-${Date.now()}`, role: 'user', message, subject: null, body: null, createdAt: new Date().toISOString() };
    setChatTurns((s) => ({ ...s, [participantId]: [...(s[participantId] || []), optimistic] }));
    setChatInput((s) => ({ ...s, [participantId]: '' }));
    setChatSending((s) => ({ ...s, [participantId]: true }));
    setChatError((s) => ({ ...s, [participantId]: null }));
    try {
      const res = await meetingsAPI.sendFollowupChat(meetingId, participantId, message);
      const data = res?.data;
      if (!data?.draft) throw new Error('No revision returned');
      setChatTurns((s) => ({ ...s, [participantId]: data.turns ?? s[participantId] }));
      setDrafts((s) => ({ ...s, [participantId]: data.draft }));
      setEditing((s) => ({ ...s, [participantId]: false }));
    } catch (err) {
      // Drop the optimistic turn and restore the message so the user can retry.
      setChatTurns((s) => ({ ...s, [participantId]: (s[participantId] || []).filter((t) => t.id !== optimistic.id) }));
      setChatInput((s) => ({ ...s, [participantId]: s[participantId] ? s[participantId] : message }));
      setChatError((s) => ({ ...s, [participantId]: err?.message || 'Refinement failed' }));
    } finally {
      setChatSending((s) => ({ ...s, [participantId]: false }));
    }
  }

  async function handleCopy(draft) {
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable; no-op */
    }
  }

  function mailtoHref(recipient, draft) {
    const to = recipient?.email ? encodeURIComponent(recipient.email) : '';
    const su = encodeURIComponent(draft.subject || '');
    const body = encodeURIComponent(draft.body || '');
    return `mailto:${to}?subject=${su}&body=${body}`;
  }

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 flex items-center gap-3 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading recipients…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="glass rounded-2xl p-6 flex items-center gap-3 text-red-600">
        <AlertCircle className="w-5 h-5" />
        {loadError}
      </div>
    );
  }

  if (recipients.length === 0) {
    return (
      <div className="glass rounded-2xl p-6 text-gray-500">
        No recipients to draft for yet. Follow-up drafts appear once the meeting has other participants.
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-5 h-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900">Follow-up emails</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Tell Rithmo what the email should accomplish, the goal or call-to-action of the call, and it reads the full transcript to draft it for that outcome. Rithmo drafts, you review and send.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Recipient picker */}
        <div className="space-y-1">
          {recipients.map((r) => {
            const has = Boolean(drafts[r.participantId]);
            const active = r.participantId === selectedId;
            return (
              <button
                key={r.participantId}
                onClick={() => setSelectedId(r.participantId)}
                className={`w-full text-left px-3 py-2 rounded-xl flex items-center gap-3 transition-colors ${
                  active ? 'bg-primary-50 ring-1 ring-primary-200' : 'hover:bg-gray-100'
                }`}
              >
                <span className="w-8 h-8 shrink-0 rounded-full bg-gradient-primary text-white text-xs font-semibold flex items-center justify-center">
                  {initials(r.displayName)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 truncate">
                    {decodeHtmlEntities(r.displayName || 'Unknown')}
                  </span>
                  {r.email && <span className="block text-xs text-gray-400 truncate">{r.email}</span>}
                </span>
                {has && <Check className="w-4 h-4 text-green-500 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Draft surface */}
        <div>
          {selected && (
            <>
              {/* Intent prompt — the email is built around this. Required before generating. */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  What should this email do? <span className="text-gray-400 font-normal">(the goal, ask, or call-to-action)</span>
                </label>
                <textarea
                  value={selectedInstruction}
                  onChange={(e) =>
                    setInstructions((s) => ({ ...s, [selected.participantId]: e.target.value }))
                  }
                  rows={3}
                  maxLength={2000}
                  placeholder={`e.g. "Thank ${decodeHtmlEntities(firstNameOf(selected.displayName))} and lock in the intro to their VP of Eng" or "Nudge them to send back the signed proposal by Friday"`}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 leading-relaxed focus:border-primary-300 focus:outline-none"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {TONES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTones((s) => ({ ...s, [selected.participantId]: t.id }))}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                        selectedTone === t.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handleGenerate(selected.participantId)}
                  disabled={generating[selected.participantId] || !selectedInstruction.trim()}
                  title={!selectedInstruction.trim() ? 'Describe what the email should accomplish first' : undefined}
                  className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {generating[selected.participantId] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : selectedDraft ? (
                    <RotateCcw className="w-4 h-4" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {generating[selected.participantId]
                    ? 'Drafting…'
                    : selectedDraft
                      ? 'Regenerate'
                      : 'Draft follow-up'}
                </button>
              </div>

              {genError[selected.participantId] && (
                <div className="mb-4 flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  {genError[selected.participantId]}
                </div>
              )}

              {!selectedDraft && !generating[selected.participantId] && (
                <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl p-8 text-center">
                  No draft yet for {decodeHtmlEntities(selected.displayName || 'this person')}. Describe what the email should accomplish above, then click Draft follow-up.
                </div>
              )}

              {selectedDraft && (
                <div className="space-y-4">
                  {/* Subject */}
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Subject</label>
                    <input
                      value={selectedDraft.subject}
                      readOnly={!editing[selected.participantId]}
                      onChange={(e) => updateDraftField(selected.participantId, 'subject', e.target.value)}
                      className={`w-full rounded-lg border px-3 py-2 text-sm font-medium text-gray-900 ${
                        editing[selected.participantId] ? 'border-primary-300 bg-white' : 'border-gray-200 bg-gray-50'
                      }`}
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Body</label>
                    <textarea
                      value={selectedDraft.body}
                      readOnly={!editing[selected.participantId]}
                      onChange={(e) => updateDraftField(selected.participantId, 'body', e.target.value)}
                      rows={12}
                      className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap ${
                        editing[selected.participantId] ? 'border-primary-300 bg-white' : 'border-gray-200 bg-gray-50'
                      }`}
                    />
                  </div>

                  {/* Provenance: personal facts the model used */}
                  {selectedDraft.usedFacts?.length > 0 && (
                    <div className="text-xs text-gray-500">
                      <span className="font-medium text-gray-600">Personal touch from memory: </span>
                      {selectedDraft.usedFacts.map((f, i) => (
                        <span key={i} className="inline-block bg-amber-50 text-amber-700 rounded-full px-2 py-0.5 mr-1 mb-1">
                          {f.text}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {editing[selected.participantId] ? (
                      <button
                        onClick={() => handleSaveEdits(selected.participantId)}
                        disabled={saving[selected.participantId]}
                        className="btn-primary flex items-center gap-2 disabled:opacity-60"
                      >
                        {saving[selected.participantId] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Save edits
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditing((s) => ({ ...s, [selected.participantId]: true }))}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                    <button onClick={() => handleCopy(selectedDraft)} className="btn-secondary flex items-center gap-2">
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    <a href={mailtoHref(selected, selectedDraft)} className="btn-secondary flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Open in mail client
                    </a>
                    {selectedDraft.edited && <span className="text-xs text-gray-400">Edited</span>}
                  </div>

                  {/* Refinement chat — keep iterating without starting over. */}
                  <div className="mt-2 border-t border-gray-200 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-primary-600" />
                      <h4 className="text-sm font-semibold text-gray-900">Refine with Rithmo</h4>
                    </div>

                    {selectedChat.length > 0 && (
                      <div className="space-y-2 mb-3 max-h-72 overflow-y-auto pr-1">
                        {selectedChat.map((t) => (
                          <div
                            key={t.id}
                            className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                                t.role === 'user'
                                  ? 'bg-primary-600 text-white rounded-br-sm'
                                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                              }`}
                            >
                              {t.message}
                            </div>
                          </div>
                        ))}
                        {chatSending[selected.participantId] && (
                          <div className="flex justify-start">
                            <div className="bg-gray-100 text-gray-500 rounded-2xl rounded-bl-sm px-3 py-2 text-sm flex items-center gap-2">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Revising the draft…
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {chatError[selected.participantId] && (
                      <div className="mb-2 flex items-center gap-2 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {chatError[selected.participantId]}
                      </div>
                    )}

                    <div className="flex items-end gap-2">
                      <textarea
                        value={chatInput[selected.participantId] || ''}
                        onChange={(e) =>
                          setChatInput((s) => ({ ...s, [selected.participantId]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendChat(selected.participantId);
                          }
                        }}
                        rows={2}
                        placeholder="Ask for a change: make it shorter, change the tone, add what they said about pricing, take a new angle…"
                        className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 leading-relaxed focus:border-primary-300 focus:outline-none resize-none"
                      />
                      <button
                        onClick={() => handleSendChat(selected.participantId)}
                        disabled={
                          chatSending[selected.participantId] ||
                          !(chatInput[selected.participantId] || '').trim()
                        }
                        title="Send (Enter)"
                        className="btn-primary flex items-center justify-center w-10 h-10 shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {chatSending[selected.participantId] ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Rithmo edits the draft above with each message and remembers the conversation. Regenerate starts fresh.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
